import { IconButton, isAnyDropdownOpen, useModal } from '@billy/design-system'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useKey } from 'react-use'

import { useInvoicingSettingsHost } from '../../../../contexts/invoicingSettingsHostContext'

interface InvoicingSettingsModalCloseButtonProps {
  id: string
}

export const InvoicingSettingsModalCloseButton = ({ id }: InvoicingSettingsModalCloseButtonProps): ReactElement => {
  const { t } = useTranslation()
  const { isLastOpened } = useModal(id)
  const { isProcessing, requestNavigation } = useInvoicingSettingsHost()

  const handleCloseButtonClick = useCallback(() => {
    requestNavigation({ type: 'close' })
  }, [requestNavigation])

  // DS Modal has no onBeforeClose, so the host uses closable={false} to stop the built-in X / overlay /
  // Escape from bypassing the dirty guard. Escape is reimplemented here the same way a closable Modal
  // would: ignore it when this modal is not on top or a dropdown is open, otherwise run the guarded close.
  const handleEscapePress = useCallback(() => {
    if (!isLastOpened || isAnyDropdownOpen()) {
      return
    }

    handleCloseButtonClick()
  }, [handleCloseButtonClick, isLastOpened])

  useKey('Escape', handleEscapePress, { target: window }, [handleEscapePress])

  return <IconButton aria-label={t('close')} disabled={isProcessing} icon="x" onClick={handleCloseButtonClick} />
}
